import React from 'react';
import PageHeader from './PageHeader';
import { SectionLeadContext } from './sectionLead';
import PageViews from './PageViews';
import { useRegisterPageUnit } from '../exports/pageUnit';
import { useRegisterPageExports } from '../exports/pageExports';
import './PageShell.css';

/**
 * PageShell — gabarit de page UNIQUE (charte).
 *
 * Une page = en-tête (PageHeader : titre + outils + actions) + corps.
 * Un hub à onglets n'est pas un autre gabarit : c'est une PageShell dont le corps
 * contient un <Tabs> + le panneau actif. On ne duplique donc rien.
 *
 * TROIS USAGES (`kind`, 2026-09-17) — ils ne changent pas le gabarit, ils
 * posent des valeurs par DÉFAUT de présentation et disent au cadre quelles
 * commandes ont un sens ici :
 *   • 'report'   — on lit : période, unité et export à la ligne de commandes.
 *   • 'entry'    — on saisit : période s'il y a lieu, ni unité ni export par
 *                  défaut (un écran de saisie n'est pas une sortie).
 *   • 'settings' — on règle : aucune commande de contexte.
 * Une page reste maîtresse : ce qu'elle passe (`unit`, `exports`) est posé,
 * ce qu'elle omet n'apparaît pas. Le gabarit organise le placement, il
 * n'impose rien.
 *
 * Trois tenues, pilotées par deux props :
 *   • par défaut  — page ouverte pleine largeur (ex-`page-table-layout is-fluid`).
 *   • framed      — encadré card (les hubs à onglets ; ex-`.fin-hub`).
 *   • embedded    — la page est rendue DANS le corps d'un hub : pas de titre
 *                   (le hub le porte déjà), pas de padding haut, et on rend un
 *                   <div> (pas un 2ᵉ <main>). Les outils/actions de la page
 *                   restent affichés.
 *
 * Props :
 *   title     : string | node  — titre H1 (masqué si embedded)
 *   subtitle  : string | node  — ligne fine sous le titre (masqué si embedded)
 *   actions   : node           — boutons/sélecteurs alignés à droite de l'en-tête
 *   views     : array          — LES COMMANDES DE PORTÉE PAGE (étage 3) :
 *                                elles pilotent TOUTE la page, donc plusieurs
 *                                cartes, et se posent SOUS LE TITRE, à
 *                                gauche (la ligne du titre garde le contexte,
 *                                court et stable). Deux formes,
 *                                `segmented` (2-3 choix fréquents) et `select`
 *                                (4 choix et plus, ou rarement changés) ; le
 *                                SUJET se déclare en dernier, sa place est
 *                                tenue. Cf. PageViews.
 *   tools     : node           — rangée d'outils contextuels sous le titre
 *   embedded  : bool           — rendu en sous-page d'un hub
 *   framed    : bool           — encadré card (hub)
 *   exportRef : ref            — posée sur la racine de la page : l'export Excel
 *                                d'une SECTION SANS ONGLET y lit les tableaux
 *                                déjà RENDUS (cf. useViewExcelExport), au lieu
 *                                d'en monter une seconde copie hors écran.
 *   timeless  : bool           — page transverse sans dimension temporelle
 *                                (paramétrage / utilitaires) : masque le
 *                                sélecteur année/mois de l'en-tête.
 *   className : string         — classes additionnelles sur la racine
 */
export default function PageShell({
  title,
  subtitle,
  actions,
  tools,
  views = null,
  kind = null,
  unit = null,
  exports: pageExports = null,
  embedded = false,
  framed = false,
  timeless = false,
  exportRef,
  className = '',
  children,
}) {
  /* Les commandes de contexte remontent au CADRE (`PageBar`), jamais posées
     dans l'écran : une sous-page embarquée ne déclare donc rien, c'est son hub
     qui parle. Un usage qui ne les porte pas les efface, ce qui évite un
     contrôle inerte sur un écran de réglages. */
  const wantsUnit = unit && kind !== 'settings';
  useRegisterPageUnit(wantsUnit ? unit : null, !embedded);
  useRegisterPageExports(pageExports || [], !embedded && !!pageExports);
  /* LE TITRE RESTE DANS L'EN-TÊTE, même sur une page à onglets (2026-09-18).
     Il descendait sur la ligne d'onglets depuis le 2026-09-01, pour gagner un
     étage avant la donnée. Le gabarit a changé depuis : la LIGNE DU TITRE
     porte la bande de contexte (exercice, unité, sortie), et sans titre une
     page à onglets ouvrait sur ses onglets, la bande flottant seule au-dessus.
     Un écran se présente donc partout de la même façon : titre et contexte,
     puis les commandes de portée page, puis les onglets, puis les cartes. */
  const headTitle = embedded ? '' : title;
  /* LES COMMANDES DE PORTÉE PAGE SE POSENT SOUS LE TITRE, À GAUCHE
     (2026-09-18). Elles fermaient la ligne du titre, où la bande de contexte
     les a rejointes : à trois commandes et un exercice, la ligne débordait et
     la bande retombait seule sur un second rang, à droite, ce qui donnait deux
     lignes mal appariées. La ligne du titre garde donc le CONTEXTE (statut du
     réel, exercice, unité, sortie), qui est court et stable ; les commandes,
     plus longues et variables, prennent le rang du dessous. */
  const viewsRow = (views && views.length) ? <PageViews items={views} /> : null;
  const hasHeader =
    (headTitle != null && headTitle !== '') || actions != null || tools != null;

  const base = framed
    ? 'page-shell page-shell--framed'
    : 'page-shell page-table-layout is-fluid';
  const cls = [base, embedded ? 'is-embedded' : '', kind ? `page-shell--${kind}` : '', className]
    .filter(Boolean).join(' ');

  // Embarqué = fragment dans le <main> du hub → <div> (un seul <main> par page).
  const Tag = embedded ? 'div' : 'main';

  /* (Une prop `column` a plafonné la ligne de titre à la largeur des cartes
     une heure durant, pour aligner leurs bords droits. Elle traitait le
     symptôme : le vide venait de la table, dont le mode `fill` étirait la
     colonne des libellés. L'en-tête reprend toute la largeur, chaque contenu
     prend la sienne, et le vide reste dehors.) */
  return (
    <SectionLeadContext.Provider value={null}>
    <Tag className={cls} ref={exportRef}>
      {hasHeader && (
        <PageHeader title={headTitle} subtitle={embedded ? undefined : subtitle} actions={actions} timeless={timeless}
                    className={viewsRow ? 'page-header--withviews' : ''}>
          {tools}
        </PageHeader>
      )}
      {!embedded && viewsRow}
      {children}
    </Tag>
    </SectionLeadContext.Provider>
  );
}
